import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { immerable, produce } from 'immer';
import { OrderedSet } from 'immutable';

import dayjs from '../../../core/configs/dayjs';
import { ProjectElementsEntity } from '../project-delete-popup/project-elements.entity';
import { ProjectMemberEntity } from './project-member.entity';

interface IProjectItemEntity {
	id: number;
	label: string;
	code: string;
	description: string;
	isFavorite: boolean;
	isDefault: boolean;
	isExpired: boolean;
	isActive: boolean;
	isAdmin: boolean;
	expirationDate: dayjs.Dayjs;
	imageUrl: string;
	memberCount: number;
	adminCount: number;
	contextCount: number;
	accessedBy: string;
	accessedByAvatar: AvatarEntity | undefined;
	accessDate: dayjs.Dayjs;
	version: string;
	tags: OrderedSet<AppTagItemEntity>;
	members: Array<ProjectMemberEntity>;
	elements: ProjectElementsEntity | undefined;
	isDeletable: boolean | undefined;
	isCritical: boolean | undefined;
	createdBy: string;
	creationDate: dayjs.Dayjs;
	updatedBy: string;
	updateDate: dayjs.Dayjs;
}

const entityDefaults: IProjectItemEntity = {
	id: -1,
	label: '',
	code: '',
	description: '',
	isFavorite: false,
	isDefault: false,
	isExpired: false,
	isActive: false,
	isAdmin: false,
	expirationDate: dayjs(),
	imageUrl: '',
	memberCount: 0,
	adminCount: 0,
	contextCount: 0,
	accessedBy: '',
	accessedByAvatar: undefined,
	accessDate: dayjs(),
	version: '0.0.0',
	tags: OrderedSet(),
	members: [],
	elements: undefined,
	isDeletable: undefined,
	isCritical: undefined,
	createdBy: '',
	creationDate: dayjs(),
	updatedBy: '',
	updateDate: dayjs(),
};

export class ProjectItemEntity implements IProjectItemEntity {
	private [immerable] = true;
	public id = entityDefaults.id;
	public label = entityDefaults.label;
	public code = entityDefaults.code;
	public description = entityDefaults.description;
	public isFavorite = entityDefaults.isFavorite;
	public isDefault = entityDefaults.isDefault;
	public isExpired = entityDefaults.isExpired;
	public isActive = entityDefaults.isActive;
	public isAdmin = entityDefaults.isAdmin;
	public expirationDate = entityDefaults.expirationDate;
	public imageUrl = entityDefaults.imageUrl;
	public memberCount = entityDefaults.memberCount;
	public adminCount = entityDefaults.adminCount;
	public contextCount = entityDefaults.contextCount;
	public accessedBy = entityDefaults.accessedBy;
	public accessedByAvatar = entityDefaults.accessedByAvatar;
	public accessDate = entityDefaults.accessDate;
	public version = entityDefaults.version;
	public tags = entityDefaults.tags;
	public members = entityDefaults.members;
	public elements = entityDefaults.elements;
	public isDeletable = entityDefaults.isDeletable;
	public isCritical = entityDefaults.isCritical;
	public createdBy = entityDefaults.createdBy;
	public creationDate = entityDefaults.creationDate;
	public updatedBy = entityDefaults.updatedBy;
	public updateDate = entityDefaults.updateDate;
	public static build(
		params: Partial<IProjectItemEntity> = {}
	): ProjectItemEntity {
		const instance = new ProjectItemEntity();
		instance.id = params.id ?? entityDefaults.id;
		instance.label = params.label ?? entityDefaults.label;
		instance.code = params.code ?? entityDefaults.code;
		instance.description = params.description ?? entityDefaults.description;
		instance.isFavorite = params.isFavorite ?? entityDefaults.isFavorite;
		instance.isDefault = params.isDefault ?? entityDefaults.isDefault;
		instance.isExpired = params.isExpired ?? entityDefaults.isExpired;
		instance.isActive = params.isActive ?? entityDefaults.isActive;
		instance.isAdmin = params.isAdmin ?? entityDefaults.isAdmin;
		instance.expirationDate =
			params.expirationDate ?? entityDefaults.expirationDate;
		instance.imageUrl = params.imageUrl ?? entityDefaults.imageUrl;
		instance.memberCount = params.memberCount ?? entityDefaults.memberCount;
		instance.adminCount = params.adminCount ?? entityDefaults.adminCount;
		instance.contextCount = params.contextCount ?? entityDefaults.contextCount;
		instance.accessedBy = params.accessedBy ?? entityDefaults.accessedBy;
		if (params.accessedBy) {
			instance.accessedByAvatar = AvatarEntity.build(params.accessedBy);
		}
		instance.accessDate = params.accessDate ?? entityDefaults.accessDate;
		instance.version = params.version ?? entityDefaults.version;
		instance.tags = params.tags ?? entityDefaults.tags;
		instance.members = params.members ?? entityDefaults.members;
		instance.elements = params.elements ?? entityDefaults.elements;
		instance.isDeletable = params.isDeletable ?? entityDefaults.isDeletable;
		instance.isCritical = params.isCritical ?? entityDefaults.isCritical;
		instance.createdBy = params.createdBy ?? entityDefaults.createdBy;
		instance.creationDate = params.creationDate ?? entityDefaults.creationDate;
		instance.updatedBy = params.updatedBy ?? entityDefaults.updatedBy;
		instance.updateDate = params.updateDate ?? entityDefaults.updateDate;
		return instance;
	}

	public compareTo(other: ProjectItemEntity): number {
		if (this.isDefault !== other.isDefault) {
			return this.isDefault ? -1 : 1;
		}
		if (this.isFavorite !== other.isFavorite) {
			return this.isFavorite ? -1 : 1;
		}
		if (!this.accessDate || !other.accessDate) {
			throw new Error('Date is null!');
		}
		return other.accessDate.valueOf() - this.accessDate.valueOf();
	}

	public setMembers(members: Array<ProjectMemberEntity>): ProjectItemEntity {
		return produce(this, (draft: ProjectItemEntity) => {
			draft.members = members;
		});
	}

	public setDeletable(isDeletable: boolean | undefined): ProjectItemEntity {
		return produce(this, (draft: ProjectItemEntity) => {
			draft.isDeletable = isDeletable;
		});
	}

	public setCritical(isCritical: boolean | undefined): ProjectItemEntity {
		return produce(this, (draft: ProjectItemEntity) => {
			draft.isCritical = isCritical;
		});
	}

	public setElements(
		elements: ProjectElementsEntity | undefined
	): ProjectItemEntity {
		return produce(this, (draft: ProjectItemEntity) => {
			draft.elements = elements;
		});
	}
}
